import { ReactElement } from 'react';

import { Refresh } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import { Box } from '@mui/material';
import { GridToolbarContainer, GridToolbarQuickFilter } from '@mui/x-data-grid-pro';

import TableBackground from 'src/assets/images/backgrounds/Table.jpg';
import Button from 'src/components/Button';
import FilterDropdown from 'src/components/FilterDropDown';

import DeleteModeButtons from './DeleteModeButtons';

export type AddRecordButtonConfig = {
    addRecordButtonText: string;
    addRecordCallBack: () => void;
};

export type DeleteModeConfig = {
    isActive: boolean;
    hasPermission?: boolean;
    onEnter: () => void;
    onCancel: () => void;
    onConfirm: () => void;
    disableConfirm?: boolean;
};

export type AddFilterButtonConfig = {
    isFilterActive?: boolean;
    handleOnClearFilters?: () => void;
    handleOnApplyFilters?: () => void;
    filters: ReactElement[];
};

type CustomToolbarProps = {
    title: string;
    addRecordButtonConfig?: AddRecordButtonConfig;
    addFilterButtonConfig?: AddFilterButtonConfig;
    serverSideFilteringEnabled?: boolean;
    handleReset?: () => void;
    deleteModeConfig?: DeleteModeConfig;
};

function CustomToolbar({
    title,
    addRecordButtonConfig,
    addFilterButtonConfig,
    serverSideFilteringEnabled = false,
    handleReset,
    deleteModeConfig,
}: Readonly<CustomToolbarProps>) {
    return (
        <GridToolbarContainer
            sx={{
                backgroundImage: `url(${TableBackground})`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center',
                backgroundSize: 'cover',
                borderRadius: '16px 0px',
                padding: '10px',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    width: '100%',
                    justifyContent: 'space-between',
                }}
            >
                <GridToolbarQuickFilter
                    data-testid={`${title ? `${title}-` : ''}search-input`}
                    sx={{
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderRadius: '4px',
                            borderColor: 'border.light',
                            borderWidth: '1px',
                            borderStyle: 'solid',
                        },
                    }}
                    variant="outlined"
                    placeholder="Search Table"
                    {...(serverSideFilteringEnabled && {
                        debounceMs: 500,
                    })}
                    parser={(input: string) => {
                        return [input];
                    }}
                />
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        width: '100%',
                        minHeight: '100%',
                        gap: 2,
                        justifyContent: 'end',
                    }}
                >
                    {addFilterButtonConfig && (
                        <FilterDropdown
                            isFilterActive={addFilterButtonConfig.isFilterActive}
                            handleOnClearFilters={addFilterButtonConfig.handleOnClearFilters}
                            handleOnApplyFilters={addFilterButtonConfig.handleOnApplyFilters}
                            filters={addFilterButtonConfig.filters}
                        />
                    )}
                    {!addFilterButtonConfig && serverSideFilteringEnabled && handleReset && (
                        <Button
                            data-testid={`reset-button`}
                            label={'Clear'}
                            onClick={handleReset}
                            startIcon={<Refresh />}
                            variant="npi-primary"
                        />
                    )}
                    {deleteModeConfig?.hasPermission && (
                        <DeleteModeButtons
                            isActive={deleteModeConfig.isActive}
                            onEnter={deleteModeConfig.onEnter}
                            onCancel={deleteModeConfig.onCancel}
                            onConfirm={deleteModeConfig.onConfirm}
                            disableConfirm={deleteModeConfig.disableConfirm}
                        />
                    )}
                    {addRecordButtonConfig && (
                        <Button
                            data-testid={`${title ? `${title}-` : ''}add-record-button`}
                            label={addRecordButtonConfig.addRecordButtonText}
                            onClick={addRecordButtonConfig.addRecordCallBack}
                            startIcon={<AddIcon />}
                            variant="npi-primary"
                        />
                    )}
                </Box>
            </Box>
        </GridToolbarContainer>
    );
}

export default CustomToolbar;
